'use client';

import { useState } from 'react';
import { LocaleLink } from '@/core/i18n';
import {
  Play, Monitor, CalendarCheck, Radio, Sparkles, BarChart3, ArrowRight,
  CheckCircle, Building2, Users, Mail, Phone,
} from 'lucide-react';
import { usePageContent } from '@/core/i18n/content';
import { DEMOS_CONTENT } from '@/core/i18n/content/demos.content';

const DEMO_ICONS = [Monitor, CalendarCheck, Radio, Sparkles, BarChart3];
const ROOM_VALUES = ['1-25', '26-75', '76-200', '201-500', '500+'];

export function DemosPage() {
  const c = usePageContent(DEMOS_CONTENT);
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', hotelName: '', roomCount: '', message: '' });

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <div className="min-h-screen bg-hms-bg">
      {/* Hero */}
      <section id="demos-hero" className="relative py-20 sm:py-28 px-5 md:px-10">
        <div className="max-w-5xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-hms-gold/10 border border-hms-gold/20 mb-6">
            <Play className="w-4 h-4 text-hms-gold" />
            <span className="text-[13px] font-medium text-hms-gold">{c.hero.badge}</span>
          </div>
          <h1 id="demos-heading" className="text-3xl sm:text-4xl lg:text-5xl font-bold text-hms-text leading-tight mb-5">{c.hero.title}</h1>
          <p id="demos-subheading" className="text-hms-textSub text-base sm:text-lg max-w-2xl mx-auto leading-relaxed">{c.hero.subtitle}</p>
        </div>
      </section>

      {/* Demo Videos Grid */}
      <section id="demos-videos-section" className="py-16 px-5 md:px-10 border-t border-hms-border/40">
        <div className="max-w-6xl mx-auto">
          <h2 id="demos-videos-heading" className="text-2xl sm:text-3xl font-bold text-hms-text text-center mb-4">{c.videos.title}</h2>
          <p className="text-hms-textSub text-center max-w-xl mx-auto mb-12">{c.videos.subtitle}</p>

          <div className="grid gap-8">
            {c.demoSections.map((demo, idx) => {
              const Icon = DEMO_ICONS[idx] ?? Monitor;
              return (
                <div key={idx} id={`demos-video-${idx}`} className="group grid grid-cols-1 lg:grid-cols-2 gap-6 p-6 rounded-2xl border border-hms-border/60 bg-hms-surface hover:border-hms-gold/30 transition-colors">
                  <div className={`relative aspect-video rounded-xl bg-hms-card border border-hms-border/40 overflow-hidden flex items-center justify-center ${idx % 2 === 1 ? 'lg:order-2' : ''}`}>
                    <div className="absolute inset-0 bg-gradient-to-br from-hms-gold/5 to-transparent" />
                    <div className="text-center z-10">
                      <div className="w-16 h-16 rounded-full bg-hms-gold/20 flex items-center justify-center mx-auto mb-3 group-hover:bg-hms-gold/30 transition-colors">
                        <Play className="w-7 h-7 text-hms-gold ml-1" />
                      </div>
                      <p className="text-sm font-semibold text-hms-textSub">{c.demoComingSoon}</p>
                      <p className="text-xs text-hms-textDim mt-1">{demo.duration}</p>
                    </div>
                  </div>
                  <div className={`flex flex-col justify-center ${idx % 2 === 1 ? 'lg:order-1' : ''}`}>
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-lg bg-hms-gold/10 flex items-center justify-center">
                        <Icon className="w-5 h-5 text-hms-gold" />
                      </div>
                      <h3 className="text-xl font-bold text-hms-text">{demo.title}</h3>
                    </div>
                    <p className="text-hms-textSub text-[14px] leading-relaxed mb-4">{demo.description}</p>
                    <ul className="space-y-2">
                      {demo.features.map((feature) => (
                        <li key={feature} className="flex items-center gap-2 text-[13px] text-hms-textSub">
                          <CheckCircle className="w-4 h-4 text-hms-green flex-shrink-0" />{feature}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Request Demo Form */}
      <section id="demos-request-section" className="py-16 px-5 md:px-10 border-t border-hms-border/40">
        <div className="max-w-4xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            <div>
              <h2 id="demos-request-heading" className="text-2xl sm:text-3xl font-bold text-hms-text mb-4">{c.request.title}</h2>
              <p className="text-hms-textSub text-[14px] leading-relaxed mb-8">{c.request.subtitle}</p>
              <ul className="space-y-3 mb-8">
                {c.request.benefits.map((benefit) => (
                  <li key={benefit} className="flex items-start gap-2.5 text-[13px] text-hms-textSub">
                    <CheckCircle className="w-4 h-4 text-hms-gold flex-shrink-0 mt-0.5" />{benefit}
                  </li>
                ))}
              </ul>
              <div className="space-y-4 text-[13px]">
                <div className="flex items-center gap-3 text-hms-textSub"><Mail className="w-4 h-4 text-hms-gold" /><span>{c.request.email}</span></div>
                <div className="flex items-center gap-3 text-hms-textSub"><Phone className="w-4 h-4 text-hms-gold" /><span>{c.request.phone}</span></div>
              </div>
            </div>

            <div className="p-6 rounded-2xl border border-hms-border/60 bg-hms-surface">
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="demos-form-name" className="block text-[12px] font-medium text-hms-textSub mb-1.5">{c.form.fullName}</label>
                    <input id="demos-form-name" type="text" value={formData.name} onChange={(e) => handleChange('name', e.target.value)} className="w-full h-11 px-3.5 rounded-lg bg-hms-card border border-hms-border text-hms-text text-[13px] placeholder:text-hms-textDim focus:border-hms-gold focus:ring-1 focus:ring-hms-gold/30 outline-none transition-colors" placeholder={c.form.namePh} />
                  </div>
                  <div>
                    <label htmlFor="demos-form-email" className="block text-[12px] font-medium text-hms-textSub mb-1.5">{c.form.workEmail}</label>
                    <input id="demos-form-email" type="email" value={formData.email} onChange={(e) => handleChange('email', e.target.value)} className="w-full h-11 px-3.5 rounded-lg bg-hms-card border border-hms-border text-hms-text text-[13px] placeholder:text-hms-textDim focus:border-hms-gold focus:ring-1 focus:ring-hms-gold/30 outline-none transition-colors" placeholder={c.form.emailPh} />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="demos-form-phone" className="block text-[12px] font-medium text-hms-textSub mb-1.5">{c.form.phoneLbl}</label>
                    <input id="demos-form-phone" type="tel" value={formData.phone} onChange={(e) => handleChange('phone', e.target.value)} className="w-full h-11 px-3.5 rounded-lg bg-hms-card border border-hms-border text-hms-text text-[13px] placeholder:text-hms-textDim focus:border-hms-gold focus:ring-1 focus:ring-hms-gold/30 outline-none transition-colors" placeholder={c.form.phonePh} />
                  </div>
                  <div>
                    <label htmlFor="demos-form-hotel" className="block text-[12px] font-medium text-hms-textSub mb-1.5">{c.form.hotelName}</label>
                    <input id="demos-form-hotel" type="text" value={formData.hotelName} onChange={(e) => handleChange('hotelName', e.target.value)} className="w-full h-11 px-3.5 rounded-lg bg-hms-card border border-hms-border text-hms-text text-[13px] placeholder:text-hms-textDim focus:border-hms-gold focus:ring-1 focus:ring-hms-gold/30 outline-none transition-colors" placeholder={c.form.hotelPh} />
                  </div>
                </div>
                <div>
                  <label htmlFor="demos-form-rooms" className="block text-[12px] font-medium text-hms-textSub mb-1.5">{c.form.rooms}</label>
                  <select id="demos-form-rooms" value={formData.roomCount} onChange={(e) => handleChange('roomCount', e.target.value)} className="w-full h-11 px-3.5 rounded-lg bg-hms-card border border-hms-border text-hms-text text-[13px] focus:border-hms-gold focus:ring-1 focus:ring-hms-gold/30 outline-none transition-colors">
                    <option value="">{c.form.selectRange}</option>
                    {c.form.roomRanges.map((range, i) => (
                      <option key={i} value={ROOM_VALUES[i]}>{range}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="demos-form-message" className="block text-[12px] font-medium text-hms-textSub mb-1.5">{c.form.message}</label>
                  <textarea id="demos-form-message" value={formData.message} onChange={(e) => handleChange('message', e.target.value)} rows={3} className="w-full px-3.5 py-2.5 rounded-lg bg-hms-card border border-hms-border text-hms-text text-[13px] placeholder:text-hms-textDim focus:border-hms-gold focus:ring-1 focus:ring-hms-gold/30 outline-none transition-colors resize-none" placeholder={c.form.messagePh} />
                </div>
                <button id="demos-form-submit" type="button" className="w-full h-11 rounded-lg bg-gradient-to-r from-hms-gold to-hms-goldLt text-black font-semibold text-[13px] hover:from-hms-goldLt hover:to-hms-gold transition-all shadow-md shadow-hms-gold/25 hover:shadow-hms-gold/40 flex items-center justify-center gap-2">
                  {c.form.submit}<ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section id="demos-cta-section" className="py-16 px-5 md:px-10 border-t border-hms-border/40">
        <div className="max-w-3xl mx-auto text-center">
          <Building2 className="w-10 h-10 text-hms-gold mx-auto mb-4" />
          <h2 className="text-2xl sm:text-3xl font-bold text-hms-text mb-4">{c.cta.title}</h2>
          <p className="text-hms-textSub text-[14px] max-w-lg mx-auto mb-8">{c.cta.subtitle}</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <LocaleLink id="demos-cta-register" href="/register-hotel" className="px-6 py-3 rounded-lg bg-gradient-to-r from-hms-gold to-hms-goldLt text-black font-semibold text-[13px] hover:from-hms-goldLt hover:to-hms-gold transition-all shadow-md shadow-hms-gold/25 flex items-center gap-2">
              {c.cta.trial}<ArrowRight className="w-4 h-4" />
            </LocaleLink>
            <LocaleLink id="demos-cta-pricing" href="/pricing" className="px-6 py-3 rounded-lg border border-hms-border text-hms-textSub hover:text-hms-text hover:border-hms-borderLt text-[13px] font-medium transition-colors">{c.cta.pricing}</LocaleLink>
          </div>
        </div>
      </section>
    </div>
  );
}
